/* Practice — the doer-effect stage.
 *
 * The staircase (02-practice rule 1): predict → run → trace → assemble →
 * complete → modify → write, plus critique (D-022) — each lesson ships the
 * rungs its concept needs, not all seven. Every item takes an answer and responds to it: the
 * research is about *interactive practice with feedback*, and an instruction to
 * "write it down" is neither. Students are at a computer; give them somewhere
 * to type.
 *
 * Adaptive, per ai-rules/02-practice rules 2a–2b:
 *   · the first step's items are the rapid diagnostic — how they go decides how
 *     much of the rest the student sees
 *   · a miss serves another item of the same kind rather than moving on
 *   · the diagnostic routes (rule 2a): all right skips to modify/write, partly
 *     right skips to assemble/complete, all wrong gets every rung. Skipped rungs
 *     are collapsed, never removed. Critique is never skipped — it is its own
 *     skill, not a support
 *
 * Never a difficulty toggle. Bottom-quartile students rate themselves at the
 * 58th–68th percentile, so a self-selected "expert mode" is chosen most by the
 * people it harms (techniques-guide.md §20).
 */

import { useState } from 'react';
import s from './Practice.module.css';
import { Kicker, Button, CodeBlock } from '../design/primitives.jsx';
import { inline } from '../content/inline.jsx';
import Exercise from './Exercise.jsx';
import { runProgram } from '../lib/runner.js';

const cx = (...v) => v.filter(Boolean).join(' ');
const norm = v => String(v).trim().toLowerCase().replace(/\s+/g, ' ').replace(/[;]$/, '');

function Item({ item, onResult }) {
  const [value, setValue] = useState('');
  const [result, setResult] = useState(null);
  const [unknown, setUnknown] = useState(false);
  /* An honest miss (D-040): routes like a wrong answer — extras served, lower
     rungs kept — without guessing. */
  const dontKnow = () => { setUnknown(true); setResult(false); onResult(item.id, false); };

  const check = (answer = value) => {
    const ok = (item.accept || []).some(a => norm(a) === norm(answer));
    setValue(String(answer));
    setResult(ok);
    onResult(item.id, ok);
  };
  /* A closed question is buttons (D-041). */
  const choices = item.choices?.map(String);

  return (
    <div className={cx(s.item, item.question && s.prose, result === true && s.right, result === false && s.wrong)}>
      {item.question
        ? <p className={s.question}>{inline(item.question)}</p>
        : <code className={s.itemCode}>{item.code}</code>}
      <div className={s.itemAnswer}>
        {choices ? (
          <div className={s.choices} role="group" aria-label={`Answer for ${item.code || item.question}`}>
            {choices.map(c => (
              <button key={c} type="button" disabled={result !== null} onClick={() => check(c)}
                      className={cx(s.choice, result !== null && !unknown && norm(c) === norm(value) && s.picked)}>
                {c}
              </button>
            ))}
          </div>
        ) : (
          <input
            className={s.input}
            value={value}
            disabled={result !== null}
            placeholder="your answer"
            aria-label={`Answer for ${item.code || item.question}`}
            onChange={e => setValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && value.trim() && result === null) check(); }}
          />
        )}
        {result === null
          ? <>
              {!choices && <Button variant="secondary" onClick={() => check()} disabled={!value.trim()}>Check</Button>}
              <button type="button" className={s.idk} onClick={dontKnow}>I don't know</button>
            </>
          : <span className={cx(s.verdict, result ? s.vRight : s.vWrong)}>
              {result ? 'right' : unknown ? 'noted' : 'not yet'}
            </span>}
      </div>
      {/* As in the lesson's questions: on a miss, what they probably thought
          first, then the rule. */}
      {result === false && !unknown && item.miss && <p className={s.why}><strong>{inline(item.miss)}</strong></p>}
      {result !== null && <p className={s.why}>{inline(item.why)}</p>}
    </div>
  );
}

function Hints({ hints }) {
  const [open, setOpen] = useState(false);
  if (!hints?.length) return null;
  return (
    <div className={s.hints}>
      <button type="button" className={s.hintToggle} aria-expanded={open} onClick={() => setOpen(o => !o)}>
        {open ? 'Hide hints' : `Stuck? ${hints.length} hint${hints.length === 1 ? '' : 's'}`}
      </button>
      {open && hints.map((h, i) => (
        <div key={i} className={s.hint}>
          <div className={s.hintWhen}>If {h.when}</div>
          <p className={s.hintSay}>{inline(h.say)}</p>
        </div>
      ))}
    </div>
  );
}

const CODE_KINDS = new Set(['complete', 'modify', 'write']);

/* complete, modify and write are the same machine — an editor, a starter,
   tests — and differ only in how much of the answer the starter already holds. */
function CodeStep({ step, lessonId }) {
  return (
    <div className={s.exercise}>
      <Exercise
        exercise={{
          kicker: step.kind,
          title: step.title,
          brief: step.prompt,
          filename: step.filename,
          exportName: step.exportName,
          starter: step.starter,
          tests: step.tests,
          success: 'Every case passes — including the two that look like edge cases and are not.',
        }}
        lessonId={lessonId}
        layout="practice"
      />
    </div>
  );
}

/* run — the prediction gate. The program runs only once every prediction above
   it is in, so what it prints is checked against a committed answer rather
   than read instead of one. */
function RunIt({ code, filename, ready }) {
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);
  const go = async () => { setBusy(true); setOut(await runProgram({ code })); setBusy(false); };
  return (
    <div className={s.run}>
      <CodeBlock filename={filename || 'program.js'}>{String(code).replace(/\n+$/, '')}</CodeBlock>
      <div className={s.runBar}>
        <Button onClick={go} disabled={!ready || busy}>{busy ? 'Running…' : 'Run it'}</Button>
        {!ready && <span className={s.runNote}>Predict first — it runs once your answers are in.</span>}
      </div>
      {out && (
        <pre className={cx(s.output, !out.ok && s.outputErr)}>
          {out.logs.map(l => l.text).join('\n')}
          {out.logs.length && out.error ? '\n' : ''}
          {out.error || (!out.logs.length ? '(printed nothing)' : '')}
        </pre>
      )}
    </div>
  );
}

/* critique — code to review, with line numbers, because the answers name lines. */
function NumberedCode({ code, filename }) {
  /* Numbers inside the block rather than in a separate gutter: a gutter has to
     match the block's line height exactly, and did not. */
  const lines = String(code || '').replace(/\n+$/, '').split('\n');
  const w = String(lines.length).length;
  const text = lines.map((l, i) => `${String(i + 1).padStart(w)}  ${l}`).join('\n');
  return <div className={s.numbered}><CodeBlock filename={filename || 'review.js'}>{text}</CodeBlock></div>;
}

/* assemble — a Parsons problem (rule 1). Lines arrive shuffled, with any
   distractors among them; the student builds the program by picking lines in
   order. The shuffle is seeded by the lesson so a reload shows the same puzzle. */
function shuffled(list, seed) {
  let h = 2166136261;
  for (const ch of String(seed)) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  const a = list.map((v, i) => ({ v, i }));
  for (let i = a.length - 1; i > 0; i--) {
    h = Math.imul(h ^ (h >>> 13), 1274126177) >>> 0;
    const j = h % (i + 1);
    [a[i], a[j]] = [a[j], a[i]];
  }
  if (a.every((x, k) => x.i === k) && a.length > 1) a.push(a.shift());   // never hand it over solved
  return a;
}

function Assemble({ step, seed, onScore }) {
  const pieces = [
    ...step.lines.map((line, i) => ({ key: `l${i}`, line, distractor: null })),
    ...(step.distractors || []).map((d, i) => ({ key: `d${i}`, line: d.line, distractor: d })),
  ];
  const pool0 = shuffled(pieces, seed + step.title).map(x => x.v);
  const [picked, setPicked] = useState([]);
  const [result, setResult] = useState(null);
  const pool = pool0.filter(p => !picked.some(q => q.key === p.key));

  const check = () => {
    const got = picked.map(p => p.line.trim());
    const ok = got.length === step.lines.length && got.every((l, i) => l === step.lines[i].trim());
    setResult(ok);
    onScore('assemble', ok ? 1 : 0, 1);
  };
  const usedDistractors = picked.filter(p => p.distractor);
  const firstWrong = picked.findIndex((p, i) => p.line.trim() !== (step.lines[i] || '').trim());

  return (
    <div className={cx(s.assemble, result === true && s.right, result === false && s.wrong)}>
      <div className={s.assembleCols}>
        <div>
          <div className={s.assembleHead}>Lines</div>
          <div className={s.lineList}>
            {pool.map(p => (
              <button type="button" key={p.key} className={s.line} disabled={result !== null}
                      onClick={() => setPicked([...picked, p])}><code>{p.line}</code></button>
            ))}
            {!pool.length && <p className={s.runNote}>All placed.</p>}
          </div>
        </div>
        <div>
          <div className={s.assembleHead}>Your program</div>
          <div className={s.lineList}>
            {picked.map((p, i) => (
              <button type="button" key={p.key}
                      className={cx(s.line, s.placed, result === false && i === firstWrong && s.lineWrong)}
                      disabled={result !== null}
                      onClick={() => setPicked(picked.filter(x => x.key !== p.key))}>
                <span className={s.lineNo}>{i + 1}</span><code>{p.line}</code>
              </button>
            ))}
            {!picked.length && <p className={s.runNote}>Click lines on the left to place them, in order. Click a placed line to take it back.</p>}
          </div>
        </div>
      </div>
      <div className={s.runBar}>
        {result === null
          ? <Button onClick={check} disabled={!picked.length}>Check</Button>
          : <>
              <span className={cx(s.verdict, result ? s.vRight : s.vWrong)}>{result ? 'right' : 'not yet'}</span>
              {!result && <Button variant="secondary" onClick={() => { setPicked([]); setResult(null); }}>Try again</Button>}
            </>}
      </div>
      {result === false && usedDistractors.map(d => (
        <p key={d.key} className={s.why}><code>{d.line.trim()}</code> — {inline(d.distractor.why)}</p>
      ))}
      {result === false && !usedDistractors.length && firstWrong >= 0 && (
        <p className={s.why}>Line {firstWrong + 1} is the first one out of place.</p>
      )}
      {result !== null && <p className={s.why}>{inline(step.why)}</p>}
    </div>
  );
}

function Step({ step, collapsed, onScore, lessonId }) {
  const [results, setResults] = useState({});
  const [open, setOpen] = useState(!collapsed);
  const [showExtra, setShowExtra] = useState(false);

  const record = (id, ok) => {
    const next = { ...results, [id]: ok };
    setResults(next);
    const items = step.items || [];
    const answered = items.filter(i => next[i.id] !== undefined).length;
    if (answered === items.length) {
      const missed = items.filter(i => next[i.id] === false).length;
      /* Rule 2b — a miss re-expands rather than moving on. */
      if (missed > 0 && step.extra?.length) setShowExtra(true);
      onScore(step.kind, items.length - missed, items.length);
    }
  };

  if (collapsed && !open) {
    return (
      <div className={cx(s.step, s.collapsed)}>
        <button type="button" className={s.expand} onClick={() => setOpen(true)}>
          <span className={s.kind}>{step.kind}</span>
          <span className={s.collapsedNote}>
            {step.title} — skipped because of how you did on the first step. Open it anyway.
          </span>
        </button>
      </div>
    );
  }

  /* A code step's heading and prompt are the exercise's own bar and brief —
     the brief stays beside the editor while the student works, so it is not
     repeated above it (Nick chose this layout, 2026-10-08). */
  const code = CODE_KINDS.has(step.kind);
  return (
    <div className={s.step}>
      {!code && (
        <>
          <div className={s.stepHead}>
            <span className={s.kind}>{step.kind}</span>
            <span className={s.stepTitle}>{step.title}</span>
          </div>
          <p className={s.prompt}>{inline(step.prompt)}</p>
        </>
      )}

      {code ? (
        <CodeStep step={step} lessonId={lessonId} />
      ) : step.kind === 'assemble' ? (
        <Assemble step={step} seed={lessonId} onScore={onScore} />
      ) : (
        <>
          {step.kind === 'critique' && <NumberedCode code={step.code} filename={step.filename} />}
          <div className={s.items}>
            {step.items.map(i => <Item key={i.id} item={i} onResult={record} />)}
          </div>
          {step.kind === 'run' && <RunIt code={step.code} filename={step.filename}
                                         ready={Object.keys(results).length === step.items.length} />}
          {step.kind === 'critique' && step.tests && <CodeStep step={step} lessonId={lessonId} />}
          {showExtra && step.extra?.length > 0 && (
            <div className={s.extra}>
              <div className={s.extraHead}>A couple more of the same kind</div>
              <div className={s.items}>
                {step.extra.map(i => <Item key={i.id} item={i} onResult={() => {}} />)}
              </div>
            </div>
          )}
        </>
      )}

      <Hints hints={step.hints} />
    </div>
  );
}

export default function Practice({ practice, lessonId }) {
  const [scores, setScores] = useState({});
  const steps = practice.steps || [];

  const onScore = (i, got, total) => setScores(p => ({ ...p, [i]: { got, total } }));

  /* Rule 2a — the first step routes. All right: skip to modify/write. Partly
     right: skip to assemble/complete. All wrong: every rung. */
  const first = scores[0];
  const route = !first ? 'all' : first.got === first.total ? 'top' : first.got > 0 ? 'middle' : 'all';
  const SKIP = { all: new Set(), middle: new Set(['predict', 'run', 'trace']),
                 top: new Set(['predict', 'run', 'trace', 'assemble', 'complete']) };
  const skipped = (step, i) => i > 0 && step.kind !== 'critique' && SKIP[route].has(step.kind);

  return (
    <section className={s.practice} aria-label="Practice">
      <div className={s.head}>
        <Kicker>Practice</Kicker>
        <h2 className={s.title}>Do the thing</h2>
        <p className={s.sub}>
          This is the part that decides whether the lesson sticks. The page above
          is preparation for it, not a substitute. Answer as you go — how you do
          on the first step decides how much of the rest you see.
        </p>
      </div>

      <div className={s.steps}>
        {steps.map((step, i) => (
          <Step
            key={i}
            step={step}
            collapsed={skipped(step, i)}
            onScore={(kind, got, total) => onScore(i, got, total)}
            lessonId={lessonId}
          />
        ))}
      </div>
    </section>
  );
}
